import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, FormControlLabel, FormGroup, Grid2, Switch } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { fitAndCorrectType } from "../BookingForm/functions/form-selection.functions";
import { namePerType } from "../FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import { useRemotePatchCalendarSync } from "../../hooks/Remote/CalendarSync/useRemotePatchCalendarSync";
import { useRemoteFetchStartMsgraph } from "../../hooks/Remote/CalendarSync/useRemoteFetchStartMsgraph";
import { useRemoteDeleteMsgraph } from "../../hooks/Remote/CalendarSync/useRemoteDeleteMsgraph";
import { FabComp, MsTeamsIconComp } from "./booking-calendarSync-integration-fab.component";
import { setSettings, setUserInformation } from "../../features/Login/slices/login.slice";
import { SeeditDialogActions } from "../Dialog/SeeditDialogActions.component";
import { SeeditDialogContent } from "../Dialog/SeeditDialogContent.component";

type P = {
  setCalendarSyncOpen: (o: boolean) => void;
};

const CalendarSyncIntegrationBooking: React.FC<P> = ({ setCalendarSyncOpen }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { userInformation, settings } = useSelector((state: RootState) => state.login);
  const {
    placeTypes,
    zoneTypes,
    msGraphEnabled,
    company: {
      meta: { workplaceBookingEnabled }
    }
  } = userInformation;
  const dispatch = useDispatch();

  const { data: msGraphStartUrl } = useRemoteFetchStartMsgraph();
  const {
    mutate: deleteMsGraph,
    isSuccess: isDeleteMsGraphSuccess,
    isError: isDeleteMsGraphError
  } = useRemoteDeleteMsgraph();
  const { mutateAsync: patchCalendarSync } = useRemotePatchCalendarSync();

  const [newValues, setNewValues] = useState<BookingType[]>([]);

  function postSettings() {
    patchCalendarSync({ enabledSyncs: newValues })
      .then(() => {
        enqueueSnackbar(t("Successfully updated the calendar sync setting"), {
          variant: "success"
        });
        setCalendarSyncOpen(false);
        dispatch(setSettings({ settings: { ...settings, enabledSyncs: newValues } }));
      })
      .catch(e => {
        const errCompanyNotAllowed = "Company does not allow calendar sync!";
        if (axios.isAxiosError(e) && e.response?.data?.message === errCompanyNotAllowed) {
          enqueueSnackbar(t("_calendarSyncCompanyNotAllowed"), { variant: "error" });
        } else {
          enqueueSnackbar(t("There was a problem activating the calendar sync"), {
            variant: "error"
          });
        }
      });
  }

  const handleAddToEnabledSync = (bookingType: BookingType) => {
    addItemToEnabledSync(newValues, setNewValues, bookingType);
  };

  const handleRemoveFromEnabledSync = (bookingType: BookingType) => {
    removeItemFromEnabledSync(newValues, setNewValues, bookingType);
  };

  const switchPerBookingType = (bookingType: BookingType) => {
    return (
      <Switch
        data-testid={`booking-calendarSync-switch-${bookingType}`}
        checked={newValues.includes(bookingType)}
        onChange={(_, checked) =>
          checked ? handleAddToEnabledSync(bookingType) : handleRemoveFromEnabledSync(bookingType)
        }
        name={t("_calendarSyncAndIntegrationsToggle", { bookingType: namePerType(bookingType, t) })}
        slotProps={{ input: { "aria-label": "secondary checkbox" } }}
      />
    );
  };

  useEffect(() => {
    if (settings?.enabledSyncs?.length) {
      setNewValues(settings.enabledSyncs as BookingType[]);
    }
  }, []);

  useEffect(() => {
    if (isDeleteMsGraphSuccess) {
      enqueueSnackbar(t("Successfully deleted the calendar sync"), { variant: "success" });
      dispatch(setUserInformation({ ...userInformation, msGraphEnabled: false }));
    }
    if (isDeleteMsGraphError) enqueueSnackbar(t("There was a server error"), { variant: "error" });
  }, [isDeleteMsGraphSuccess, isDeleteMsGraphError]);

  return (
    <>
      <SeeditDialogContent>
        <Grid2 container>
          <Box sx={{ pl: 0.625, pt: 1.25, mr: 2.5 }}>
            <FormGroup row>
              {fitAndCorrectType(BookingType.WORKPLACE, placeTypes, zoneTypes) &&
                workplaceBookingEnabled && (
                  <Box sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={switchPerBookingType(BookingType.WORKPLACE)}
                      label={t("_calendarSyncAndIntegrationsToggle", {
                        bookingType: t("Workplace")
                      })}
                    />
                  </Box>
                )}
              {fitAndCorrectType(BookingType.PARKINGPLACE, placeTypes, zoneTypes) && (
                <Box sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={switchPerBookingType(BookingType.PARKINGPLACE)}
                    label={t("_calendarSyncAndIntegrationsToggle", { bookingType: t("Parking") })}
                  />
                </Box>
              )}
              {fitAndCorrectType(
                BookingType.ELECTRICCHARGINGSTATIONPLACE,
                placeTypes,
                zoneTypes
              ) && (
                <Box sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={switchPerBookingType(BookingType.ELECTRICCHARGINGSTATIONPLACE)}
                    label={t("_calendarSyncAndIntegrationsToggle", {
                      bookingType: t("Electric Charging")
                    })}
                  />
                </Box>
              )}
              {fitAndCorrectType(BookingType.CONFERENCEZONE, placeTypes, zoneTypes) && (
                <Box sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={switchPerBookingType(BookingType.CONFERENCEZONE)}
                    label={t("_calendarSyncAndIntegrationsToggle", {
                      bookingType: t("_bookTypeMeetingRoom")
                    })}
                  />
                </Box>
              )}
            </FormGroup>
          </Box>

          <Grid2 container columnGap={1}>
            <FabComp
              isConnected={msGraphEnabled}
              iconComponent={MsTeamsIconComp}
              startLinkUrl={msGraphStartUrl}
              deleteLink={deleteMsGraph}
            />
          </Grid2>
        </Grid2>
      </SeeditDialogContent>

      <SeeditDialogActions>
        <Button data-testid="post-calendarSync-and-integration-btn" onClick={postSettings}>
          {t("Confirm")}
        </Button>
      </SeeditDialogActions>
    </>
  );
};

export default CalendarSyncIntegrationBooking;

export function addItemToEnabledSync(
  newValues: BookingType[],
  setNewValues: (v: BookingType[]) => void,
  item: BookingType
) {
  setNewValues([...newValues, item]);
}

export function removeItemFromEnabledSync(
  newValues: BookingType[],
  setNewValues: (v: BookingType[]) => void,
  item: BookingType
) {
  setNewValues(newValues.filter(v => v !== item));
}
